import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import type { AdminOrderTransaction } from "../../lib/api/adminTransactionsData";
import { formatAmount } from "../../utils/FormatPerson";
import {
  formatOrderSummary,
  getOrderDisplayDate,
  orderTypeBadgeClass,
  statusBadgeClass,
} from "./ordersOrderTable";

type Props = {
  open: boolean;
  onClose: () => void;
  transaction: AdminOrderTransaction | null;
};

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function fmtScalar(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "number") return formatAmount(v);
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v, null, 2);
  return String(v);
}

function formatTimestamp(iso: unknown): string {
  const s = str(iso);
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return s;
  }
}

function humanizeFieldKey(raw: string): string {
  return raw
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^\w/, (c) => c.toUpperCase());
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-[#2C363E] bg-[#161C22]/55 p-4 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.035)] sm:p-5">
      <h3 className="mb-4 flex items-center gap-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#869AA9]">
        <span
          className="h-px w-7 shrink-0 bg-gradient-to-r from-[#F28A0F]/90 to-transparent"
          aria-hidden
        />
        {title}
      </h3>
      <div className="divide-y divide-[#23292F]">{children}</div>
    </section>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 py-3.5 first:pt-0 last:pb-0 sm:flex-row sm:justify-between sm:gap-6 sm:items-start">
      <dt className="text-[12px] font-medium text-[#7D8F9C] shrink-0 min-w-[7.5rem]">
        {label}
      </dt>
      <dd className="text-[13px] font-medium leading-snug text-[#EEF2F6] text-left sm:text-right break-words min-w-0">
        {value}
      </dd>
    </div>
  );
}

function CopyTextButton({
  text,
  showToast = true,
}: {
  text: string;
  showToast?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      if (showToast) toast.success("Copied to clipboard");
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void onCopy();
      }}
      className="inline-flex shrink-0 items-center rounded-md border border-[#3A4852]/90 bg-[#1C2227] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#AEBCC6] transition hover:border-[#0B84CB]/50 hover:bg-[#273037] hover:text-[#E7EBEE] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-secondary_button"
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function InlineId({
  label,
  value,
  mono = true,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  if (!value)
    return <KV label={label} value={<span className="text-[#5C6B76]">—</span>} />;
  return (
    <div className="flex flex-col gap-2 py-3.5 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <dt className="text-[12px] font-medium text-[#7D8F9C] shrink-0 min-w-[7.5rem]">
        {label}
      </dt>
      <dd className="flex min-w-0 flex-1 flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-2">
        <span
          className={
            mono
              ? "text-left font-mono text-[12px] leading-relaxed text-[#C6D7E4] break-all sm:text-right"
              : "text-left text-[13px] leading-relaxed text-[#EEF2F6] break-words sm:text-right"
          }
        >
          {value}
        </span>
        <CopyTextButton text={value} showToast={false} />
      </dd>
    </div>
  );
}

function Badge({
  variant,
  raw,
  children,
}: {
  variant: "type" | "status";
  raw: unknown;
  children: React.ReactNode;
}) {
  const cls =
    variant === "type" ? orderTypeBadgeClass(raw) : statusBadgeClass(raw);
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${cls}`}
    >
      {children}
    </span>
  );
}

function PayoutItemCard({
  payload,
  index,
}: {
  payload: unknown;
  index: number;
}) {
  if (payload !== null && typeof payload === "object" && !Array.isArray(payload)) {
    const o = payload as Record<string, unknown>;
    const entries = Object.entries(o);
    return (
      <div className="rounded-lg border border-[#2C363E] bg-[#0D1114]/80 p-3 sm:p-4">
        <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-[#617889]">
          Payout {index + 1}
        </p>
        <div className="divide-y divide-[#23292F]">
          {entries.map(([key, val]) => (
            <KV
              key={key}
              label={humanizeFieldKey(key)}
              value={<ValueCell value={val} />}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-[#2C363E] bg-[#0D1114]/80 p-4">
      <pre className="max-h-40 overflow-auto text-left font-mono text-[11px] leading-relaxed text-[#AEBCC6]">
        {fmtScalar(payload)}
      </pre>
    </div>
  );
}

function ValueCell({ value }: { value: unknown }) {
  if (value !== null && typeof value === "object") {
    return (
      <pre className="max-h-36 overflow-auto text-left font-mono text-[11px] leading-relaxed text-[#C6D7E4] sm:text-right">
        {fmtScalar(value)}
      </pre>
    );
  }
  return <span>{fmtScalar(value)}</span>;
}

export default function AdminOrderDetailModal({
  open,
  onClose,
  transaction,
}: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => closeRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !transaction) return null;

  const tx = transaction;
  const ot = str(tx.orderType).toLowerCase();
  const summary = formatOrderSummary(tx);
  const headlineDate = getOrderDisplayDate(tx);
  const typeLabel = str(tx.orderType) || "Order";
  const statusLabel = str(tx.status) || "—";
  const idShort =
    tx._id.length > 12 ? `${tx._id.slice(0, 8)}…` : tx._id;

  const shownKeys = new Set<string>([
    "_id",
    "userId",
    "orderType",
    "status",
    "createdAt",
    "updatedAt",
    "processedAt",
    "asset",
    "amount",
    "amountUSDT",
    "amountNGN",
    "currency",
    "balanceDeducted",
    "from",
    "to",
    "fromDeexUser",
    "txid",
    "fees",
    "withdrawalId",
    "network",
    "toAddress",
    "toDeexUser",
    "usdtAmount",
    "isPending",
    "idempotencyKey",
    "payoutId",
    "payout",
    "provider",
    "__v",
  ]);

  const remainder = Object.keys(tx).filter((k) => !shownKeys.has(k));

  const uid = str(tx.userId);
  const processedAtShown = tx.processedAt != null && str(tx.processedAt) !== "";
  const hasRefs =
    (tx.idempotencyKey != null && str(tx.idempotencyKey) !== "") ||
    (tx.withdrawalId != null && str(tx.withdrawalId) !== "") ||
    (tx.txid != null && str(tx.txid) !== "") ||
    (tx.payoutId != null && String(tx.payoutId) !== "");

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center p-0 sm:p-6"
      role="presentation"
    >
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-detail-title"
        className="relative z-[101] flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-[#2C363E] bg-[#13181B] shadow-[0_-8px_40px_-12px_rgba(0,0,0,0.85)] sm:max-h-[86vh] sm:rounded-2xl sm:shadow-2xl"
      >
        <header className="shrink-0 border-b border-[#2C363E]/90 bg-gradient-to-br from-[#1a222d] via-[#141a20] to-[#13181B] px-4 pb-5 pt-4 sm:px-6 sm:pb-6 sm:pt-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="type" raw={tx.orderType}>
                  {typeLabel}
                </Badge>
                <Badge variant="status" raw={tx.status}>
                  {statusLabel}
                </Badge>
              </div>
              <h2
                id="order-detail-title"
                className="font-sora text-lg font-bold leading-snug capitalize text-white sm:text-xl"
              >
                <span>{typeLabel}</span>
                <span className="font-normal normal-case text-[#5C6B76]">
                  {" "}
                  ·{" "}
                </span>
                <span className="font-mono text-[15px] font-semibold tracking-tight text-[#C6D7E4] sm:text-[16px]">
                  {idShort}
                </span>
              </h2>
              <p className="text-[13px] font-medium leading-relaxed text-[#B8C5CE]">
                {summary}
              </p>
              <p className="text-[11px] text-[#617889]">
                Last activity ·{" "}
                <span className="text-[#8FA3AF]">{headlineDate}</span>
              </p>
              <InlineId label="Order ID" value={tx._id} />
              {uid ? <InlineId label="User ID" value={uid} /> : null}
            </div>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-xl border border-[#3A4852] bg-[#1C2227]/80 px-3.5 py-2 text-[12px] font-semibold text-[#E7EBEE] transition hover:border-[#5C6B76] hover:bg-[#273037]"
            >
              Close
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 scrollbar-thin scrollbar-track-[#13181B] scrollbar-thumb-[#3A4852] sm:px-6">
          <div className="space-y-5 font-manrope">
            <SectionCard title="Timeline">
              <KV label="Created" value={formatTimestamp(tx.createdAt)} />
              <KV label="Updated" value={formatTimestamp(tx.updatedAt)} />
              {processedAtShown ? (
                <KV label="Processed" value={formatTimestamp(tx.processedAt)} />
              ) : null}
            </SectionCard>

            {hasRefs ? (
              <SectionCard title="References">
                {tx.idempotencyKey != null && str(tx.idempotencyKey) !== "" ? (
                  <InlineId label="Idempotency key" value={str(tx.idempotencyKey)} />
                ) : null}
                {tx.withdrawalId != null && str(tx.withdrawalId) !== "" ? (
                  <InlineId label="Withdrawal ID" value={str(tx.withdrawalId)} />
                ) : null}
                {tx.txid != null && str(tx.txid) !== "" ? (
                  <InlineId label="Tx hash" value={str(tx.txid)} />
                ) : null}
                {tx.payoutId != null && String(tx.payoutId) !== "" ? (
                  <InlineId label="Payout ID" value={String(tx.payoutId)} />
                ) : null}
              </SectionCard>
            ) : null}

            {ot === "sell" ? (
              <SectionCard title="Sell · amounts">
                <KV label="Asset" value={fmtScalar(tx.asset)} />
                <KV label="Amount" value={fmtScalar(tx.amount)} />
                <KV label="Amount USDT" value={fmtScalar(tx.amountUSDT)} />
                <KV label="Amount NGN" value={fmtScalar(tx.amountNGN)} />
                <KV label="Balance deducted" value={fmtScalar(tx.balanceDeducted)} />
              </SectionCard>
            ) : null}

            {ot === "swap" ? (
              <SectionCard title="Swap">
                <KV label="From" value={<ValueCell value={tx.from} />} />
                <KV label="To" value={<ValueCell value={tx.to} />} />
                <KV label="Balance deducted" value={fmtScalar(tx.balanceDeducted)} />
              </SectionCard>
            ) : null}

            {ot === "deposit" ? (
              <SectionCard title="Deposit">
                <KV label="Currency" value={fmtScalar(tx.currency)} />
                <KV label="Amount" value={fmtScalar(tx.amount)} />
                <KV label="Fees" value={fmtScalar(tx.fees)} />
                <KV label="Amount NGN" value={fmtScalar(tx.amountNGN)} />
                <KV label="From Deex user" value={fmtScalar(tx.fromDeexUser)} />
                <KV label="Provider" value={fmtScalar(tx.provider)} />
              </SectionCard>
            ) : null}

            {ot === "withdrawal" ? (
              <SectionCard title="Withdrawal">
                <KV label="Currency" value={fmtScalar(tx.currency)} />
                <KV label="Amount" value={fmtScalar(tx.amount)} />
                <KV label="USDT amount" value={fmtScalar(tx.usdtAmount)} />
                <KV label="Fees" value={fmtScalar(tx.fees)} />
                <KV label="Amount NGN" value={fmtScalar(tx.amountNGN)} />
                <KV label="Network" value={fmtScalar(tx.network)} />
                {str(tx.toAddress) ? (
                  <InlineId label="To address" value={str(tx.toAddress)} />
                ) : (
                  <KV label="To address" value="—" />
                )}
                <KV label="To Deex user" value={fmtScalar(tx.toDeexUser)} />
                <KV label="Pending" value={fmtScalar(tx.isPending)} />
                <KV label="Balance deducted" value={fmtScalar(tx.balanceDeducted)} />
                <KV label="Provider" value={fmtScalar(tx.provider)} />
              </SectionCard>
            ) : null}

            {Array.isArray(tx.payout) && tx.payout.length > 0 ? (
              <section className="space-y-3">
                <h3 className="flex items-center gap-2.5 px-0.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#869AA9]">
                  <span
                    className="h-px w-7 shrink-0 bg-gradient-to-r from-[#F28A0F]/90 to-transparent"
                    aria-hidden
                  />
                  Payouts
                </h3>
                <div className="space-y-3">
                  {tx.payout.map((p, i) => (
                    <PayoutItemCard key={i} payload={p} index={i} />
                  ))}
                </div>
              </section>
            ) : null}

            {remainder.length > 0 ? (
              <SectionCard title="Additional fields">
                {remainder.map((k) => (
                  <KV key={k} label={humanizeFieldKey(k)} value={<ValueCell value={tx[k]} />} />
                ))}
              </SectionCard>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
